import { useEffect, useState } from 'react'
import { parseTags } from '../data/tags'
import type { Category } from '../data/types'

interface Props {
  category: Category
  /** Tags already in use, for autocomplete. */
  knownTags?: string[]
  onStart: (details: { note: string | null; tags: string[] }) => void
  onClose: () => void
}

/** What a long press opens: start this category with a note and tags already on it. */
export function StartSheet({ category, knownTags = [], onStart, onClose }: Props) {
  const [note, setNote] = useState('')
  const [tags, setTags] = useState('')

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    // Not dismissed by the backdrop: a stray tap should not lose the note.
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Start ${category.label}`}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-6"
    >
      <div className="pb-safe w-full max-w-md rounded-t-2xl border border-line bg-surface p-4 pb-6 sm:rounded-2xl sm:pb-5">
        <header className="flex items-center gap-2 pb-3">
          <span
            className="size-2.5 rounded-full"
            style={{ backgroundColor: category.color }}
            aria-hidden="true"
          />
          <h2 className="text-[15px] font-semibold">Start {category.label}</h2>
        </header>

        <textarea
          autoFocus
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Note"
          rows={2}
          className="mb-2 w-full resize-none rounded-lg border border-line bg-bg px-2.5 py-2 text-[14px] outline-none focus:border-line-strong"
        />
        <input
          value={tags}
          list="start-known-tags"
          onChange={(event) => setTags(event.target.value)}
          placeholder="tags, comma separated"
          className="w-full rounded-lg border border-line bg-bg px-2.5 py-2 text-[14px] outline-none focus:border-line-strong"
        />
        <datalist id="start-known-tags">
          {knownTags.map((known) => (
            <option key={known} value={known} />
          ))}
        </datalist>

        <footer className="flex items-center gap-2 pt-4 pb-2">
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-lg border border-line px-3 py-2 text-[14px] font-medium text-dim"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onStart({ note: note.trim() || null, tags: parseTags(tags) })
            }
            className="rounded-lg bg-text px-3 py-2 text-[14px] font-semibold text-bg transition-transform duration-150 active:scale-[0.98]"
          >
            Start
          </button>
        </footer>
      </div>
    </div>
  )
}
